import React, { useCallback, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import c from 'classnames';
import { Url } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import DeviationFlag from '@wix/da-ds/pkg/Indicators/DeviationFlag';
import { formatDate, DATE_FORMATS } from '@wix/da-ds/pkg/Time/functions';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import {
  BiData,
  BiContextProvidedValue,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import UserLink from '../../../../../User/Link/UserLink';
import LegacyTextRenderer from '../../../../../TextRenderer/LegacyTextRenderer';
import { ResourceType } from '../../../../../types/resource';
import { getRecipientForDeviation } from '../../../../../coreBadge/utils';
import {
  CoreBadgeType,
  AwardBadgeSource,
} from '../../../../../coreBadge/types';
import { usePushModal } from '../../../../../utils/hooks/usePushModal';
import { ModalType } from '../../../../../Modals/redux/types';

import s from './DeviationFlagCard.scss';

interface Props {
  variant: 'daily-deviation' | 'hype' | 'stash';
  deviation: PapiDeviation;
}

const DeviationFlagCard: React.FC<Props> = ({ variant, deviation }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useContext(SessionContext);
  const pushAwardsModal = usePushModal(ModalType.RECEIVED_AWARDS);
  const hypeOnClick = useCallback(() => {
    pushAwardsModal({
      badges: deviation.extended?.awardedBadges || {},
      typeId: ResourceType.DEVIATION,
      itemId: deviation.deviationId,
      recipient: getRecipientForDeviation(deviation),
      source: AwardBadgeSource.ACTIONBAR_BUTTON,
      defaultActiveType: CoreBadgeType.HYPE,
    });
  }, [pushAwardsModal, deviation]);

  let title, subtitle, cta, ctaLink, ctaOnClick, ctaBiData, description;

  switch (variant) {
    case 'daily-deviation':
      if (!deviation.dailyDeviation) {
        return null;
      }
      const { body, time, giver, suggester } = deviation.dailyDeviation;

      title = t('actionbar.flags.dailyDeviation.title');
      subtitle = t('actionbar.flags.dailyDeviation.subtitle', {
        date: formatDate(time, DATE_FORMATS.Date.Full),
      });
      cta = t('actionbar.flags.dailyDeviation.cta');
      ctaLink = Url.browseDailyDeviationsLink();
      description = (
        <>
          <LegacyTextRenderer className={s['dd-body']} html={body} />
          <div className={s['dd-attribution']}>
            <div className={s['giver']}>
              <Trans i18nKey="actionbar.flags.dailyDeviation.featuredBy">
                Featured by
                <UserLink user={giver} className={s['user-link']} />
              </Trans>
            </div>
            {suggester && (
              <div className={s['suggester']}>
                <Trans i18nKey="actionbar.flags.dailyDeviation.suggestedBy">
                  Suggested by
                  <UserLink user={suggester} className={s['user-link']} />
                </Trans>
              </div>
            )}
          </div>
        </>
      );
      break;

    case 'hype':
      const hypeKbUrl =
        'https://www.deviantartsupport.com/en/article/what-are-badges-and-fragments';
      title = t('actionbar.flags.hype.title');
      cta = t('actionbar.flags.hype.cta');
      ctaOnClick = hypeOnClick;
      ctaBiData = BiData<DefaultBiEvent>({
        evid: 2,
        typeid: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
        click_info: ReferralType.BADGES_LOG_HYPE,
      });
      description = (
        <Trans i8nKey="actionbar.flags.hype.description">
          Deviations awarded with Hype badges are promoted for more deviants to
          see.
          <a
            href={hypeKbUrl}
            target="_blank"
            rel="noreferrer"
            className={s['hype-kb-link']}
          >
            Learn more
          </a>
        </Trans>
      );
      break;

    case 'stash':
      const stashKbUrl =
        'https://www.deviantartsupport.com/kb/en/article/what-is-stash-3391708';
      title = t('actionbar.flags.stash.title');

      description = (
        <>
          <p>{t('actionbar.flags.stash.description')}</p>
          <p>
            <a href={stashKbUrl} target="_blank" rel="noreferrer">
              {t('common.learnMore')}
            </a>
          </p>
        </>
      );
      break;

    default:
      return null;
  }
  return (
    <div className={c(s['root'], 'ds-surface-tertiary')}>
      <DeviationFlag variant={variant} size="large" className={s['flag']} />
      <div className={s['content']}>
        <Heading className={s['title']}>{title}</Heading>
        {subtitle && <div className={s['subtitle']}>{subtitle}</div>}
        <div className={s['description']}>{description}</div>
        {isLoggedIn && cta && (
          <TextButton
            href={ctaLink}
            onClick={ctaOnClick}
            biData={ctaBiData}
            size="medium"
            variant="approval-green"
            noHorizontalPaddings
          >
            {cta}
          </TextButton>
        )}
      </div>
    </div>
  );
};
DeviationFlagCard.displayName = 'DeviationFlagCard';

export default DeviationFlagCard;
